import { CustomModal } from '../../../pages/kiosk/tablet/OperationRoomTabletV1';
import { RouterlessModal } from '@/se/components/Modal';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { getRoom } from '../../common/transducers';
import { getNestedValue } from '@/se/utilities/data/object';
import { useMutation, useSubscription } from '@apollo/client';
import { listNursing } from '../../../../graph/rooms';
import { discharge } from '../../../../graph/patients';
import { useSetRoomMutation } from '@/hooks/useSetRoomMutation';
import get from 'lodash/get';
import find from 'lodash/find';
import filter from 'lodash/filter';
import { OR, PACU, PRE_OP } from '../../room/enums';
import { RoomPicker } from '../RoomPicker';
import Button from '@material-ui/core/Button';
import { useToaster } from '../../../core/Toaster';
import Icon from '@mdi/react';
import { mdiAccountArrowRightOutline, mdiAccountHeartOutline } from '@mdi/js';
import withStyles from '@material-ui/core/styles/withStyles';
import { useScope } from '@/hooks/useScope';
import { useRetainedBeaconDischarge } from '@/hooks/useRetainedBeaconDischarge';
import { RetainedBeaconChoiceDialog } from './RetainedBeaconChoiceDialog';

const stopPropagation = fn => e => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (fn) {
    return fn(e);
  }
};

const filterOR = rooms => filter(rooms, ({ type }) => type === OR);

const RoomConfirmModal = ({ handleConfirm, handleCancel, patientName, roomType }) => {
  const { data } = useSubscription(listNursing);
  const room = useMemo(() => find(get(data, 'rooms'), room => get(room, 'type') === roomType), [data, roomType]);
  const roomName = get(room, 'name');

  const ReadyPrompt = useMemo(
    () => CustomModal(`Are you sure you want to move ${patientName} to ${roomName}?`, true),
    [patientName, roomName]
  );

  const onConfirm = () => handleConfirm(get(room, 'id'));

  return (
    <RouterlessModal onClose={stopPropagation(handleCancel)}>
      {roomType === OR ? (
        <RoomPicker
          rooms={filterOR(get(data, 'rooms', []))}
          onCancel={stopPropagation(handleCancel)}
          onChoose={handleConfirm}
        />
      ) : (
        <ReadyPrompt handleConfirm={stopPropagation(onConfirm)} handleCancel={stopPropagation(handleCancel)} />
      )}
    </RouterlessModal>
  );
};

export const ConfirmDischargeModal = ({ handleConfirm, handleCancel, patientId, patientName }) => {
  const scope = useScope();
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const retainedBeacon = useRetainedBeaconDischarge({
    patientId,
    enabled: scope?.hospital?.modules?.retainedBeaconInpatient?.enabled === true,
    surface: 'patient-location',
  });
  const beginRetainedBeaconCheck = retainedBeacon.begin;
  const Prompt = useMemo(
    () => CustomModal(`Are you sure you want to discharge patient ${patientName}?`, true),
    [patientName]
  );

  useLayoutEffect(() => {
    beginRetainedBeaconCheck();
  }, [beginRetainedBeaconCheck]);

  const cancel = () => {
    retainedBeacon.reset();
    handleCancel();
  };

  const confirm = async choice => {
    if (choice == null && !retainedBeacon.canSubmit) return;
    setSubmitting(true);
    try {
      await handleConfirm(retainedBeacon.prepareSubmission(choice));
      cancel();
    } catch (error) {
      if (error?.graphQLErrors?.some(graphQLError => graphQLError.message === 'RetainedDischargeConflict')) {
        setErrorMessage(
          'The patient is no longer eligible to keep this beacon. Review the schedule, then choose Standard discharge or cancel.'
        );
      } else {
        setErrorMessage('Discharge failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (retainedBeacon.requiresChoice) {
    return (
      <RetainedBeaconChoiceDialog
        open
        eligibility={retainedBeacon.eligibility}
        onChoose={confirm}
        onCancel={cancel}
        working={submitting}
        errorMessage={errorMessage}
      />
    );
  }

  return (
    <RouterlessModal onClose={stopPropagation(cancel)}>
      <Prompt
        handleConfirm={stopPropagation(() => confirm(null))}
        handleCancel={stopPropagation(cancel)}
        errorMessage={errorMessage}
      />
    </RouterlessModal>
  );
};

const flow = {
  WAITING_ROOM: PRE_OP,
  PRE_OP: OR,
  OR: PACU,
};

const roomTypeLabels = {
  PRE_OP: 'PreOp',
  OR: 'OR',
  PACU: 'Recovery',
};

const MoveButton = withStyles(theme => ({
  root: {
    justifyContent: 'end',
    backgroundColor: theme.palette.background.default,
  },
}))(Button);

const LocationChangeWidget = ({ data: { id, room, patientName } }) => {
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null);
  const [doSetRoom] = useSetRoomMutation();
  const [doSetDischarge] = useMutation(discharge);
  const toaster = useToaster();
  const nextRoom = flow[get(room, 'type')];

  const withBusy = operation => async roomId => {
    setModal(false);
    setBusy(true);

    try {
      const result = await operation({ variables: { id, roomId: roomId } });
      if (result?.data?.setRoomForPatient === false) {
        toaster.warning('Room change is already in progress for this patient. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const withBusy2 = operation => async keepBeacon => {
    setBusy(true);

    try {
      await operation({ variables: { id, keepBeacon } });
    } finally {
      setBusy(false);
    }
  };

  const setHere = withBusy(doSetRoom);
  const setDischarge = withBusy2(doSetDischarge);

  const closeModal = stopPropagation(() => setModal(null));

  const openMoveModal = stopPropagation(() =>
    setModal(
      <RoomConfirmModal
        roomType={nextRoom}
        handleConfirm={setHere}
        handleCancel={closeModal}
        patientName={patientName}
      />
    )
  );

  const openDischargeModal = stopPropagation(() =>
    setModal(
      <ConfirmDischargeModal
        handleConfirm={setDischarge}
        handleCancel={closeModal}
        patientId={id}
        patientName={patientName}
      />
    )
  );

  if (!nextRoom && !modal) {
    return (
      <MoveButton
        startIcon={<Icon path={mdiAccountHeartOutline} size={1} style={{ opacity: 0.5 }} />}
        color="secondary"
        fullWidth
        disableElevation
        variant="outlined"
        disabled={busy}
        onClick={openDischargeModal}
      >
        {busy ? 'Discharging…' : 'Discharge'}
      </MoveButton>
    );
  }

  return modal ? (
    modal
  ) : (
    <MoveButton
      startIcon={<Icon path={mdiAccountArrowRightOutline} size={1} style={{ opacity: 0.5 }} />}
      color="primary"
      fullWidth
      disableElevation
      variant="outlined"
      disabled={busy}
      onClick={openMoveModal}
    >
      {busy ? 'Moving…' : `Move To ${roomTypeLabels[nextRoom]}`}
    </MoveButton>
  );
};

export const actionColumn = {
  title: 'Action',
  style: {
    position: 'sticky',
    right: 0,
  },
  lens: data => ({
    id: data.id,
    room: getRoom(data.room),
    patientName: getNestedValue('name', data) || '',
  }),
  Component: LocationChangeWidget,
};
